import * as ReduxToolkit from "@reduxjs/toolkit";
import { ShiftReportsList, TripsReportsRow } from "../../api";
import { ReserveTripMap } from "../../api/models/ReserveTripMap";
import { Order } from "../../components/table/sort";
import {
    DisplayCond,
    newDisplayCond,
} from "../../components/table/sortableTable";
import { today } from "../../utils/time";

export interface ReserveShiftsState {
    startDate: string;
    endDate?: string;
    driverIds: number[];
    officeId?: number;
    needGuidanceOnly: boolean;
    shiftReports: ShiftReportsList | null;
    displayCond: DisplayCond;
}

export function initReserveShiftsState(): ReserveShiftsState {
    return {
        startDate: today(),
        endDate: "",
        driverIds: [],
        officeId: -1,
        needGuidanceOnly: false,
        shiftReports: null,
        displayCond: newDisplayCond("shift_date", "desc", 0, 30),
    };
}

export const reserveShiftsSlice = ReduxToolkit.createSlice({
    name: "shiftMonitoring",
    initialState: initReserveShiftsState(),
    reducers: {
        resetState: (state) => {
            state = initReserveShiftsState();
            return state;
        },
        update_shift_reports: (
            state,
            action: ReduxToolkit.PayloadAction<ShiftReportsList | null>
        ) => {
            state.shiftReports = action.payload;
            state.displayCond.page.no = 0;
            return state;
        },
        update_driver_ids: (
            state,
            action: ReduxToolkit.PayloadAction<number[]>
        ) => {
            state.driverIds = action.payload;
            return state;
        },
        update_start_date: (
            state,
            action: ReduxToolkit.PayloadAction<string>
        ) => {
            state.startDate = action.payload;
            return state;
        },
        update_need_guidance_only: (
            state,
            action: ReduxToolkit.PayloadAction<boolean>
        ) => {
            state.needGuidanceOnly = action.payload;
            return state;
        },
        update_end_date: (
            state,
            action: ReduxToolkit.PayloadAction<string>
        ) => {
            state.endDate = action.payload;
            return state;
        },
        update_office_id: (
            state,
            action: ReduxToolkit.PayloadAction<number | undefined>
        ) => {
            state.officeId = action.payload;
            return state;
        },
        update_sort_cond: (
            state,
            action: ReduxToolkit.PayloadAction<[string, Order]>
        ) => {
            state.displayCond.sort.key = action.payload[0];
            state.displayCond.sort.order = action.payload[1];
        },
        update_page_cond: (
            state,
            action: ReduxToolkit.PayloadAction<[number, number]>
        ) => {
            state.displayCond.page.no = action.payload[0];
            state.displayCond.page.numRows = action.payload[1];
        },
    },
});

export interface ReserveTripsState {
    driverId: number;
    driverCode: string;
    shiftDate: string;
    driverName: string;
    shiftMin: string;
    naviUsageRate: number;
    needGuidanceHermesHeader: string;
    trips?: TripsReportsRow[] | null;
    selectedTrip?: TripsReportsRow | null;
    displayCond: DisplayCond;
}

export function initTripState(): ReserveTripsState {
    return {
        driverId: -1,
        driverCode: "-1",
        shiftDate: "",
        driverName: "",
        shiftMin: "",
        naviUsageRate: -1,
        needGuidanceHermesHeader: "",
        selectedTrip: null,
        displayCond: newDisplayCond("unladen_start_time", "asc", 0, 20),
    };
}

export const reserveTripsSlice = ReduxToolkit.createSlice({
    name: "trips",
    initialState: initTripState(),
    reducers: {
        resetState: (state) => {
            state = initTripState();
            return state;
        },
        update_selection_state: (
            state,
            action: ReduxToolkit.PayloadAction<ReserveTripsState>
        ) => {
            state.driverId = action.payload.driverId;
            state.driverCode = action.payload.driverCode;
            state.shiftDate = action.payload.shiftDate;
            state.driverName = action.payload.driverName;
            state.shiftMin = action.payload.shiftMin;
            state.naviUsageRate = action.payload.naviUsageRate;
            state.needGuidanceHermesHeader =
                action.payload.needGuidanceHermesHeader;
            return state;
        },
        update_trips: (state, action: ReduxToolkit.PayloadAction<any>) => {
            state.trips = action.payload;
            state.displayCond.page.no = 0;
            return state;
        },
        update_sort_cond: (
            state,
            action: ReduxToolkit.PayloadAction<[string, Order]>
        ) => {
            state.displayCond.sort.key = action.payload[0];
            state.displayCond.sort.order = action.payload[1];
        },
        update_page_cond: (
            state,
            action: ReduxToolkit.PayloadAction<[number, number]>
        ) => {
            state.displayCond.page.no = action.payload[0];
            state.displayCond.page.numRows = action.payload[1];
        },
    },
});

export interface ReserveTripMapState {
    isValid: boolean;
    res: ReserveTripMap;
}

export function initTripMapState(): ReserveTripMapState {
    return {
        isValid: false,
        res: {
            unladenStartLoc: "",
            unladenEndLoc: "",
            unladenShape: "",
            boardingType: "",
            routes: [],
            breaks: [],
            terminalStatus: [],
            driverCancels: [],
        },
    };
}

export const reserveTripMapSlice = ReduxToolkit.createSlice({
    name: "tripMap",
    initialState: initTripMapState(),
    reducers: {
        resetState: (state) => {
            state = initTripMapState();
            return state;
        },
        updateMap: (
            state,
            action: ReduxToolkit.PayloadAction<ReserveTripMapState | null>
        ) => {
            if (!action.payload) {
                state = initTripMapState();
                return state;
            }
            state = action.payload;
            return state;
        },
    },
});
